import { useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Receipt, Scale, Wallet } from 'lucide-react'
import { useVehicleFinancialSummary } from '@/hooks/useVehicleFinancialSummary'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

interface MetricProps {
  label: string
  value: string
  icon: React.ElementType
  tone: 'neutral' | 'green' | 'red' | 'muted'
  hint?: string
}

function Metric({ label, value, icon: Icon, tone, hint }: MetricProps) {
  const toneClass = {
    neutral: 'text-foreground',
    green: 'text-green-700',
    red: 'text-red-700',
    muted: 'text-muted-foreground',
  }[tone]
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={14} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className={cn('mt-2 text-lg font-semibold tabular-nums', toneClass)}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

interface Props {
  vehicleId: string
}

export function VehicleFinancialSummary({ vehicleId }: Props) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const params = {
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(`${endDate}T23:59:59`).toISOString() }),
  }

  const { data, isLoading } = useVehicleFinancialSummary(vehicleId, params)

  return (
    <section className="space-y-4 rounded-lg border border-border p-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Resumo financeiro</h2>
          <p className="text-xs text-muted-foreground">
            Receita dos fretes − pago a motoristas − despesas operacionais
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Data inicial"
          />
          <input
            type="date"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="Data final"
          />
        </div>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Calculando…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label="Receita de fretes"
              value={formatBRL(data.freights.totalRevenue)}
              icon={TrendingUp}
              tone="green"
              hint={`${data.freights.count} ${data.freights.count === 1 ? 'frete' : 'fretes'}`}
            />
            <Metric
              label="Pago a motoristas"
              value={formatBRL(data.freights.totalDriverPay)}
              icon={Wallet}
              tone="muted"
              hint={`Margem bruta: ${formatBRL(data.freights.grossMargin)}`}
            />
            <Metric
              label="Despesas operacionais"
              value={formatBRL(data.expenses.total)}
              icon={Receipt}
              tone="red"
              hint={`${data.expenses.count} ${data.expenses.count === 1 ? 'lançamento' : 'lançamentos'}`}
            />
            <Metric
              label="Resultado líquido"
              value={formatBRL(data.netResult)}
              icon={Scale}
              tone={Number.parseFloat(data.netResult) >= 0 ? 'green' : 'red'}
            />
          </div>

          {data.expenses.byCategory.length > 0 ? (
            <div className="mt-2">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Despesas por categoria
              </h3>
              <ul className="divide-y divide-border rounded-md border border-border">
                {data.expenses.byCategory.map((c) => (
                  <li
                    key={c.categoryId}
                    className="flex items-center justify-between px-4 py-2 text-sm"
                  >
                    <div>
                      <span className="text-foreground">{c.categoryName}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {c.count} {c.count === 1 ? 'lançamento' : 'lançamentos'}
                      </span>
                    </div>
                    <span className="font-medium tabular-nums text-red-700">
                      −{formatBRL(c.total)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Nenhuma despesa cadastrada para este veículo no período.
              {' '}
              <Link
                to={`/operational-expenses`}
                className="text-primary hover:underline"
              >
                Lançar agora
              </Link>
            </p>
          )}
        </>
      )}
    </section>
  )
}
